import { Navigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuth } from "@/contexts/AuthContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { resolveAppUserRole } from "@/lib/permissions";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { DOC_BY_PATH } from "./documentationSources";
import { createDocMarkdownComponents } from "./docMarkdownComponents";
import { normalizeDocRouteKey } from "./docPathUtils";
import { DocumentationOverview } from "./overview/DocumentationOverview";
import { canViewIntegratorDocumentation } from "./canViewIntegratorDocumentation";

const DOC_HOME_CRUMB = { label: "Documentation", href: "/documentation" };

export default function DocumentationPage() {
  const { "*": splat } = useParams();
  const { user } = useAuth();
  const { isTenantAdmin } = useAccessControl();
  const userRole = resolveAppUserRole(user);
  const key = normalizeDocRouteKey((splat ?? "").replace(/\/$/, ""));

  if (key === "") {
    return <DocumentationOverview />;
  }

  if (key === "public-opac" && !canViewIntegratorDocumentation(userRole, { isTenantAdmin })) {
    return <Navigate to="/documentation" replace />;
  }

  const entry = DOC_BY_PATH[key];
  const markdownComponents = createDocMarkdownComponents(key);

  if (!entry) {
    return (
      <PageContainer width="wide">
        <PageHeader
          title="Page not found"
          description="This guide is not available in the in-app help center yet."
          breadcrumbItems={[DOC_HOME_CRUMB, { label: "Page not found" }]}
          showSearch={false}
          titleSize="md"
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer width="wide">
      <PageHeader
        title={entry.title}
        description="Part of the PhiloLMS help center."
        breadcrumbItems={[DOC_HOME_CRUMB, { label: entry.title }]}
        showSearch={false}
        titleSize="md"
      />

      <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm animate-fade-in-up sm:p-6">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {entry.markdown}
        </ReactMarkdown>
      </article>
    </PageContainer>
  );
}
